import type { AssetAllocationSlice } from '../api/chartDataTypes';
import { SnapshotValueSelector, submitGuidance } from './SnapshotValueSelector';

const getSliceKey = (slice: AssetAllocationSlice) => slice.label;

export const ASSET_ALLOCATION_SUBMIT_GUIDANCE = submitGuidance('asset type');

type AssetAllocationSelectorProps = {
    /** Snapshot slices from the asset_allocation payload. Order is irrelevant —
     *  the selector sorts by `label` ascending internally. */
    slices: AssetAllocationSlice[];
    /** Prefix for the `<select>` id so multiple snapshot selectors on a page
     *  never collide. */
    idPrefix: string;
    /** Called only when the user presses Submit after picking an asset type.
     *  Selecting the same option again and re-submitting still fires so the
     *  parent can re-announce. */
    onSelectedSliceChange: (slice: AssetAllocationSlice) => void;
};

/**
 * Thin `SnapshotValueSelector` wrapper for the Asset Allocation chart's slice
 * picker — see `SnapshotValueSelector` for the shared Submit-gating behavior
 * (WCAG 2.2 3.2.2 On Input).
 */
export function AssetAllocationSelector({ slices, idPrefix, onSelectedSliceChange }: AssetAllocationSelectorProps) {
    return (
        <SnapshotValueSelector<AssetAllocationSlice>
            values={slices}
            getKey={getSliceKey}
            idPrefix={idPrefix}
            fieldLabel='Data Series'
            placeholder='Select data series'
            selectedAriaLabel='Data series'
            hint='Select an asset type, then press Submit to display its allocation.'
            disabledSubmitAriaLabel='Submit — select an asset type to continue'
            readySubmitAriaLabel={(label) => `Submit and display allocation for ${label}`}
            onSubmit={onSelectedSliceChange}
        />
    );
}
